import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import type { FC, ReactNode } from 'react';
import { Credits } from '@/app/components/icons/Credits';
import { Loading } from '@/app/components/icons/Loading';
import { SITE } from '@/app/constants';
import { useGlobalModal, useUserNexus } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubsPlanType } from '@/app/types';
import { cn, formatPriceDiscount, getSubscriptionUrl, reportEvent } from '@/app/utils';
import { useLocale, useRouter } from '@/i18n/routing';

const MIN_COUNT_NEED_BUY = 20;

const CreditsStyleMap: Record<SubsPlanType, string> = {
  free: 'text-[#1AC3FF]',
  standard_v2: 'bg-[linear-gradient(270deg,#78A3FF_0%,#E5F2FF_100%)]',
  premium_v2: 'bg-[linear-gradient(270deg,#BA9D64_0%,#FFF7D0_100%)]',
  ultimate_v2: 'bg-[linear-gradient(90deg,#EAEBFF_0%,#877DFF_100%)]',
  team_v2: 'text-white',
};

const first_discount_bg_url =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/First-time-Offer-bg-5880a227.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/First-time-Offer-bg-5880a227.webp';

export const CreditsButton: FC = () => {
  const router = useRouter();
  const t = useTranslations('payments');
  const locale = useLocale();

  const { userInfo, creditsInfo, isTeamSpace, maxProductDiscount } = useStore((state) => ({
    userInfo: state.userInfo,
    creditsInfo: state.creditsInfo,
    isTeamSpace: state.isTeamSpace,
    maxProductDiscount: state.maxProductDiscount,
  }));

  const { isSubOneTimeOffer } = useUserNexus();
  const isFirstDiscount = isSubOneTimeOffer;
  const { open: openCreditDetailsDialog } = useGlobalModal('creditDetailsDialog');

  if (!userInfo) return null;

  const plan_type: SubsPlanType = (userInfo?.subs_plan as SubsPlanType) ?? 'free';

  const getSubscriptionTargetUrl = (): string => {
    if (isSubOneTimeOffer) {
      return getSubscriptionUrl(isTeamSpace, 'subscription-period=subscription_monthly');
    }
    if (creditsInfo && creditsInfo.total <= MIN_COUNT_NEED_BUY && plan_type !== 'free') {
      return getSubscriptionUrl(isTeamSpace, 'tab=credits');
    }
    return getSubscriptionUrl(isTeamSpace);
  };

  const renderSubscribeText = () => {
    if (!creditsInfo) return t('subscription');
    if (plan_type === 'free') {
      return t('subscription');
    }
    if (creditsInfo.total > MIN_COUNT_NEED_BUY) {
      return t(plan_type);
    }
    return t('navPurchase');
  };

  const renderPromotionContent = (): ReactNode => {
    if (isFirstDiscount) {
      return (
        <div
          className="bg-center bg-cover bg-clip-text bg-no-repeat font-bold text-sm text-transparent"
          style={{ backgroundImage: `url(${first_discount_bg_url})` }}
        >
          {t.rich('firstDiscount', {
            discount: formatPriceDiscount(maxProductDiscount, locale),
            span: (chunks) => <span className="inline-flex h-[22px] items-center justify-center">{chunks}</span>,
          })}
        </div>
      );
    }

    return (
      <div
        className={cn(
          'relative flex h-auto flex-shrink-0 cursor-pointer items-center bg-clip-text font-medium text-sm text-transparent',
          CreditsStyleMap[plan_type],
        )}
        data-status={!creditsInfo || (creditsInfo && creditsInfo.total <= MIN_COUNT_NEED_BUY) ? 'highlight' : ''}
      >
        {renderSubscribeText()}
      </div>
    );
  };

  if (isTeamSpace) {
    return (
      <div
        className="flex h-9 cursor-pointer select-none items-center justify-center gap-2 rounded-8 border border-system-white08 bg-system-white04 pr-2 pl-3"
        onClick={() => openCreditDetailsDialog()}
      >
        <Credits variant="team_v2" className="text-sm" theme="light" />
        <span className={cn('bg-clip-text font-medium text-sm text-transparent', CreditsStyleMap.team_v2)}>
          {creditsInfo === null ? <Loading className="mr-2 animate-spin text-[#0FA7FF]" /> : creditsInfo.total}
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-9 select-none items-center justify-center gap-2 rounded-8 border border-system-white08 bg-system-white04 px-3">
      <div className="flex cursor-pointer items-center justify-center" onClick={() => openCreditDetailsDialog()}>
        <Credits variant={plan_type} className="text-sm" theme="light" />
        <span
          className={cn(
            'ml-2 bg-clip-text font-medium text-sm text-transparent',
            plan_type === 'free' ? 'text-[#1AC3FF]' : CreditsStyleMap[plan_type],
          )}
        >
          {creditsInfo === null ? <Loading className="mr-2 animate-spin text-[#0FA7FF]" /> : creditsInfo.total}
        </span>
      </div>
      <div className="h-[10px] w-[1px] bg-[rgba(217,217,217,0.6)]" />
      <div
        className="flex cursor-pointer items-center justify-center gap-2"
        onClick={() => {
          router.push(getSubscriptionTargetUrl());
          reportEvent('page_view', { page_name: 'my-subscription', from_page: 'header_credits_button' });
          reportEvent('vip_price_exposure', { from_page: 'header nav credits button' });
          sendGAEvent('event', 'subscription_exposure', { from_page: 'header nav credits button' });
        }}
      >
        {renderPromotionContent()}
      </div>
    </div>
  );
};
